import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { LoaderES } from '../../LoaderES';
import { variablesPath } from '../../../variables/Variables';
import moment from 'moment';

const API_URL = variablesPath.API_URL;
const LICENSE_API_KEY = 'dwells-lic-2026-xK9mP3rT';

const axiosLic = axios.create({
    baseURL: API_URL,
    headers: { 'X-License-Key': LICENSE_API_KEY },
});

// -------------------------------------------------------

const statusBadge = (status) => {
    const map = {
        pending:  { label: 'Pendiente', bg: 'rgba(234,179,8,0.15)',   border: 'rgba(234,179,8,0.5)',   color: '#eab308' },
        approved: { label: 'Aprobado',  bg: 'rgba(34,197,94,0.15)',   border: 'rgba(34,197,94,0.5)',   color: '#22c55e' },
        revoked:  { label: 'Revocado',  bg: 'rgba(239,68,68,0.15)',   border: 'rgba(239,68,68,0.5)',   color: '#ef4444' },
    };
    const s = map[status] || { label: status, bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', color: '#aaa' };
    return (
        <span style={{
            padding: '3px 10px', borderRadius: '20px',
            background: s.bg, border: `1px solid ${s.border}`,
            color: s.color, fontSize: '0.75rem', fontWeight: 700,
        }}>
            {s.label}
        </span>
    );
};

const fmt = (d) => d ? moment(d).format('DD/MM/YY HH:mm') : '—';
const shortId = (hash) => hash ? '…' + hash.slice(-10) : '—';

// -------------------------------------------------------

const Licencias = ({ broken, collapsed, mobile, user, colors }) => {
    const [rows, setRows]           = useState([]);
    const [isLoading, setLoading]   = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected]   = useState(null);
    const [approveNotes, setNotes]  = useState('');
    const [filtro, setFiltro]       = useState('');

    const token = JSON.parse(localStorage.getItem('token') || '{}');
    const isAdmin = token?.nb_rol_idn === '1' || token?.tipo === '1';

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosLic.get('license/list');
            setRows(Array.isArray(data) ? data : []);
        } catch {
            toast.warning('No se pudo cargar la lista de licencias');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        fetchList();
        const interval = setInterval(fetchList, 60000);
        return () => clearInterval(interval);
    }, [fetchList, isAdmin]);

    // --- Acciones ---

    const handleApprove = async () => {
        if (!selected) return;
        try {
            await axiosLic.put(`license/approve/${selected.id}`, {
                approvedBy: user,
                notes: approveNotes,
            });
            toast.success(`Licencia #${selected.id} aprobada`);
            setShowModal(false);
            setSelected(null);
            setNotes('');
            fetchList();
        } catch {
            toast.error('Error al aprobar la licencia');
        }
    };

    const handleRevoke = async (row) => {
        if (!window.confirm(`¿Revocar la licencia de ${row.operadora} — ${row.rig}?`)) return;
        try {
            await axiosLic.put(`license/revoke/${row.id}`, { revokedBy: user });
            toast.success(`Licencia #${row.id} revocada`);
            fetchList();
        } catch {
            toast.error('Error al revocar la licencia');
        }
    };

    const handleRestore = async (row) => {
        if (!window.confirm(`¿Restaurar (→ pendiente) la licencia de ${row.operadora} — ${row.rig}?`)) return;
        try {
            await axiosLic.put(`license/restore/${row.id}`);
            toast.success(`Licencia #${row.id} restaurada a pendiente`);
            fetchList();
        } catch {
            toast.error('Error al restaurar la licencia');
        }
    };

    const handleToggleAutoApprove = async (row) => {
        try {
            await axiosLic.put(`license/auto-approve/${row.id}`, { value: !row.auto_approve_reinstall });
            fetchList();
        } catch {
            toast.error('Error al cambiar auto-aprobación');
        }
    };

    // --- Guard ---
    if (!isAdmin) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>
                <p>No tiene permisos para acceder a esta sección.</p>
            </div>
        );
    }

    if (isLoading && rows.length === 0) return <LoaderES />;

    // --- Filtrado simple ---
    const q = filtro.toLowerCase();
    const filtered = q
        ? rows.filter(r =>
            (r.operadora || '').toLowerCase().includes(q) ||
            (r.rig       || '').toLowerCase().includes(q) ||
            (r.operador  || '').toLowerCase().includes(q) ||
            (r.status    || '').toLowerCase().includes(q)
          )
        : rows;

    const pendingCount = rows.filter(r => r.status === 'pending').length;

    // -------------------------------------------------------
    return (
        <>
            {/* Titulo + filtro */}
            <div className="row mb-3 align-items-center">
                <div className="col-12 col-md-6">
                    <h5 style={{ color: colors.colorFont, margin: 0 }}>
                        🔑 Licencias Desktop
                        {pendingCount > 0 && (
                            <span style={{
                                marginLeft: '10px', padding: '2px 8px',
                                background: 'rgba(234,179,8,0.2)', border: '1px solid #eab308',
                                borderRadius: '12px', color: '#eab308', fontSize: '0.75rem', fontWeight: 700,
                            }}>
                                {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
                            </span>
                        )}
                    </h5>
                </div>
                <div className="col-12 col-md-4 mt-2 mt-md-0">
                    <input
                        className="form-control form-control-sm"
                        placeholder="Filtrar..."
                        value={filtro}
                        onChange={e => setFiltro(e.target.value)}
                        style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: `1px solid ${colors.colorFont}44`,
                            color: colors.colorFont,
                        }}
                    />
                </div>
                <div className="col-12 col-md-2 mt-2 mt-md-0 text-end">
                    <button
                        className="btn btn-sm"
                        style={{ color: colors.colorFont, border: `1px solid ${colors.colorFont}44` }}
                        onClick={fetchList}
                        title="Recargar"
                    >
                        <i className="fa-solid fa-rotate-right" />
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-hover" style={{ fontSize: mobile ? '12px' : '14px', color: colors.colorFont }}>
                    <thead>
                        <tr style={{ borderBottom: `2px solid ${colors.colorFont}33` }}>
                            <th>#</th>
                            <th>Operadora</th>
                            <th>Rig</th>
                            <th>Operador</th>
                            {!mobile && <th>Machine ID</th>}
                            <th>Estado</th>
                            {!mobile && <th>Solicitado</th>}
                            {!mobile && <th>Última valid.</th>}
                            {!mobile && <th>Aprobado por</th>}
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan="10" style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
                                    {isLoading ? 'Cargando...' : 'Sin registros'}
                                </td>
                            </tr>
                        ) : filtered.map((row) => (
                            <tr key={row.id} style={{ borderBottom: `1px solid ${colors.colorFont}18` }}>
                                <td>{row.id}</td>
                                <td>{row.operadora}</td>
                                <td>{row.rig}</td>
                                <td>{row.operador}</td>
                                {!mobile && <td><code style={{ fontSize: '11px' }}>{shortId(row.machine_id_hash)}</code></td>}
                                <td>{statusBadge(row.status)}</td>
                                {!mobile && <td>{fmt(row.requested_at)}</td>}
                                {!mobile && <td>{fmt(row.last_validated_at)}</td>}
                                {!mobile && <td>{row.approved_by || row.revoked_by || '—'}</td>}
                                <td>
                                    <div className="btn-group btn-group-sm">
                                        {row.status === 'pending' && (
                                            <button
                                                className="btn btn-success btn-sm"
                                                title="Aprobar"
                                                onClick={() => { setSelected(row); setShowModal(true); }}
                                            >
                                                <i className="fa-solid fa-check" />
                                            </button>
                                        )}
                                        {row.status === 'approved' && (
                                            <>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    title="Revocar"
                                                    onClick={() => handleRevoke(row)}
                                                >
                                                    <i className="fa-solid fa-ban" />
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${row.auto_approve_reinstall ? 'btn-info' : 'btn-outline-secondary'}`}
                                                    title={row.auto_approve_reinstall ? 'Auto-aprobar: ON' : 'Auto-aprobar: OFF'}
                                                    onClick={() => handleToggleAutoApprove(row)}
                                                >
                                                    <i className="fa-solid fa-rotate" />
                                                </button>
                                            </>
                                        )}
                                        {row.status === 'revoked' && (
                                            <button
                                                className="btn btn-warning btn-sm"
                                                title="Restaurar a pendiente"
                                                onClick={() => handleRestore(row)}
                                            >
                                                <i className="fa-solid fa-arrow-rotate-left" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal Aprobar */}
            {showModal && selected && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px',
                }}>
                    <div style={{
                        background: '#1e2530', border: '1px solid rgba(5,187,242,0.4)',
                        borderRadius: '10px', width: '100%', maxWidth: '440px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                        fontFamily: '"Lato", sans-serif',
                    }}>
                        <div style={{
                            background: 'rgba(34,197,94,0.08)', borderBottom: '2px solid #22c55e',
                            padding: '16px 20px',
                        }}>
                            <h5 style={{ margin: 0, color: '#22c55e' }}>✓ Aprobar Licencia</h5>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
                                <p style={{ margin: '0 0 4px', color: '#c9d6e0', fontSize: '14px' }}><strong>Operadora:</strong> {selected.operadora}</p>
                                <p style={{ margin: '0 0 4px', color: '#c9d6e0', fontSize: '14px' }}><strong>Rig:</strong> {selected.rig}</p>
                                <p style={{ margin: '0 0 4px', color: '#c9d6e0', fontSize: '14px' }}><strong>Operador:</strong> {selected.operador}</p>
                                <p style={{ margin: 0, color: '#8892a4', fontSize: '12px' }}><strong>Machine ID:</strong> {shortId(selected.machine_id_hash)}</p>
                            </div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#8892a4', marginBottom: '6px', textTransform: 'uppercase' }}>
                                Notas (opcional)
                            </label>
                            <textarea
                                rows={3}
                                style={{
                                    width: '100%', padding: '8px 10px',
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(5,187,242,0.3)',
                                    borderRadius: '6px', color: '#e2e8f0', fontSize: '14px',
                                    resize: 'vertical', boxSizing: 'border-box',
                                }}
                                placeholder="Ej: Instalación autorizada por gerencia"
                                value={approveNotes}
                                onChange={e => setNotes(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                            <button
                                style={{
                                    flex: 1, padding: '10px',
                                    background: 'transparent', color: '#8892a4',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
                                }}
                                onClick={() => { setShowModal(false); setSelected(null); setNotes(''); }}
                            >
                                Cancelar
                            </button>
                            <button
                                style={{
                                    flex: 1, padding: '10px',
                                    background: '#22c55e', color: '#fff',
                                    border: 'none', borderRadius: '6px',
                                    cursor: 'pointer', fontWeight: 700, fontSize: '14px',
                                }}
                                onClick={handleApprove}
                            >
                                ✓ Aprobar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Licencias;
